import { useEffect, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarBlank as Calendar,
  ArrowRight,
  CaretRight as ChevronRight,
  Warning,
  WarningCircle,
  Info,
  CurrencyGbp,
  CheckSquare,
  Clock,
  Briefcase,
} from '@phosphor-icons/react';
import { fetchOperationalAlerts, fetchDashboardMetrics } from '@/lib/meApi';
import { getGreeting } from '@/lib/greeting';
import { usePreferences } from '@/lib/preferences';
import { useAuthStore } from '@/features/auth/store';
import { canRecordHearingOutcome, canAllocateFunds } from '@/lib/pupilUiBoundary';
import { useDiaryStore, todayKey as getTodayKey } from '@/lib/diaryStore';
import { useHearingsStore } from '@/lib/hearingsStore';
import { useMattersStore } from '@/lib/mattersStore';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { AgendaPanel } from '@/components/shared/AgendaPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { BriefsInbox } from '@/components/shared/BriefsInbox';
import { ConferenceApprovals } from '@/components/conferences/ConferenceApprovals';
import { MorningBrief } from '@/components/daily/MorningBrief';
import { SetupContinuation } from '@/components/shared/SetupContinuation';
import { PolicyNudge } from '@/components/shared/PolicyNudge';
import { MatterQuickSwitch } from '@/components/shared/MatterQuickSwitch';
import { MoneyRequiringAttention } from '@/components/shared/MoneyRequiringAttention';
import { FundsToAllocate } from '@/components/shared/FundsToAllocate';
import { useChambersMode } from '@/lib/useChambersMode';
import { onCalendarUpdate, useWebSocketStore } from '@/lib/websocket';
import { fetchHearingsNeedingAttention, type HearingNeedingAttention } from '@/lib/hearingsApi';
import { DiaryWarningsPanel } from '@/components/shared/DiaryWarningsPanel';

/**
 * Barrister Dashboard — Operational Command Center
 *
 * Purpose: Answer ONE question clearly: "What needs my attention right now?"
 *
 * Shows only:
 * - Court today / tomorrow
 * - Hearings needing preparation
 * - Operational alerts
 * - Key metrics (overdue tasks, deadlines, unpaid fees, active matters)
 * - Briefs inbox (if in chambers)
 *
 * Does NOT show:
 * - Full matters list (→ Matters page)
 * - Activity log (→ Reports page)
 * - Practice financials (→ Reports page)
 * - Full schedule (→ Diary page)
 */

export default function BarristerDashboardPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const chambersMode = useChambersMode();
  // The person's own timezone (or the practice's), so the greeting and date read as their day.
  const { effective: { timezone: tz, locale } } = usePreferences();

  const fetchEvents = useDiaryStore((s) => s.fetchEvents);
  const wsConnected = useWebSocketStore((s) => s.connected);

  // Keep diary data fresh
  useEffect(() => {
    fetchEvents();
    const interval = setInterval(fetchEvents, 300_000);
    return () => clearInterval(interval);
  }, [fetchEvents]);
  useEffect(() => {
    const unsub = onCalendarUpdate(() => fetchEvents());
    return unsub;
  }, [wsConnected, fetchEvents]);

  const allHearings = useHearingsStore((s) => s.hearings);
  const fetchHearings = useHearingsStore((s) => s.fetchHearings);
  useEffect(() => { fetchHearings(); }, [fetchHearings]);

  const matters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);
  const mattersError = useMattersStore((s) => s.error);
  useEffect(() => { fetchMatters(); }, [fetchMatters]);

  // Filter hearings to current user
  const myHearings = allHearings.filter(
    (h) => h.leadBarristerUserId === user?.id || h.juniorBarristerUserId === user?.id,
  );

  const todayKey = getTodayKey();

  // Hearings without outcomes — operational debt
  const pendingOutcomeHearings = myHearings.filter(
    (h) =>
      h.date < todayKey &&
      !h.outcome &&
      ['scheduled', 'confirmed', 'in_progress'].includes(h.status),
  );

  // Dashboard metrics
  const metricsQ = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: fetchDashboardMetrics,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
  });
  const metrics = metricsQ.data;

  // Hearings needing preparation (next 7 days)
  const barristerId = user?.barristerId ?? null;
  const hearingsNeedingAttentionQ = useQuery<HearingNeedingAttention[]>({
    queryKey: ['hearings-needing-attention', barristerId],
    queryFn: () => fetchHearingsNeedingAttention({ barristerId: barristerId ?? undefined, daysAhead: 7 }),
    enabled: !!barristerId,
    staleTime: 60_000,
  });
  const hearingsNeedingAttention = hearingsNeedingAttentionQ.data ?? [];

  // My active matters count
  const myMatters = matters.filter(
    (m) => m.leadBarristerUserId === user?.id || m.juniorBarristerUserId === user?.id,
  );
  const myActiveMatters = myMatters.filter((m) => m.status === 'active' || m.status === 'listed');

  // ── A failed fetch must not read as good news ──────────────────────────────────────────────
  //
  // Every section below hides itself when it has nothing: the stat strip is `{metrics && …}`,
  // the preparation card is `{…length > 0 && …}`, and the alerts panel returns null on an empty
  // list. A failed request produces exactly the same emptiness, so the dashboard said "no
  // hearings need preparation" and "nothing needs your attention" when in truth it had not been
  // told. Active matters was worse: it reads the matters STORE, so a failed load rendered a
  // literal `0`.
  //
  // A 401/403/404 is a deliberate refusal, not a connection problem — counting it would put a
  // permanent banner on the dashboard of any role the endpoint does not serve. Same rule, and
  // the same banner, as the matter page (pages/admin/MatterDetailPage.tsx).
  const isLoadFailure = (q: { isError: boolean; error: unknown }) => {
    const status = (q.error as { response?: { status?: number } } | null)?.response?.status;
    return q.isError && status !== 401 && status !== 403 && status !== 404;
  };
  const mattersFailed = !!mattersError;
  // Mirrors REPORT_ROLES (reports.routes.ts): of the barrister types only silk and junior are
  // served. Used for the Practice reports shortcut below.
  const canUseReports = ['silk', 'junior'].includes(user?.memberType ?? '');
  const failedSections = [
    ...(isLoadFailure(metricsQ) ? ([['your figures', () => void metricsQ.refetch()]] as const) : []),
    ...(isLoadFailure(hearingsNeedingAttentionQ)
      ? ([['hearing preparation', () => void hearingsNeedingAttentionQ.refetch()]] as const)
      : []),
    ...(mattersFailed ? ([['matters', () => void fetchMatters()]] as const) : []),
  ];

  return (
    <div className="space-y-6">

      {failedSections.length > 0 && (
        <div role="alert" className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          <span>
            Couldn't load {failedSections.map(([label]) => label).join(', ')} — those sections are hidden or showing a dash, not a real zero. This is a connection problem, not an all-clear.
          </span>
          <Button variant="outline" size="sm" onClick={() => failedSections.forEach(([, retry]) => retry())}>
            Try again
          </Button>
        </div>
      )}

      {/* Onboarding continuation — guides a new barrister until their practice is set up,
          then disappears (renders null at 100%). Kept at the very top for new users;
          invisible once the practice is set up. */}
      {/* Chambers policy nudge is chambers-only — a solo/single-firm barrister has no
          policy-issuing chambers, so never surface it to them. */}
      {!chambersMode.isSolo && <PolicyNudge />}
      <SetupContinuation />

      {/* ════════════════════════════════════════════════════════════
          HEADER — morning greeting
         ════════════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {getGreeting(tz)}{user?.firstName ? `, ${user.firstName}` : ''}
          </h1>
          <p className="text-sm text-muted-foreground">
            {/* Same timezone as the greeting above, or the two contradict each other. */}
            {new Date().toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz })}
          </p>
        </div>
        {/* Shown in BOTH workspace types. Recording leave is a solo feature too (owner
            decision, 2026-10-21): the server always granted it, and suppressing the shortcut
            here while also omitting the nav item left Settings → Diary as the only route in. */}
        <Button variant="outline" size="sm" onClick={() => navigate('/barrister/availability')}>
          Availability
        </Button>
      </div>

      {/* ════════════════════════════════════════════════════════════
          NEXT 24 HOURS — hearings & upcoming events, highlighted at the
          TOP. This is the first thing a barrister sees each morning:
          "here is your day." Everything below (cases at a glance, then
          money / activity / tasks) is the follow-on detail.
         ════════════════════════════════════════════════════════════ */}
      <AgendaPanel basePath="/barrister" />

      {/* Personal Diary Intelligence — my own warnings (double-book, blocked, unprepared,
          overdue fee, dormant). Quiet when nothing needs flagging. Consumes the canonical
          /operations/intelligence (scope=mine); no detection in the UI. */}
      <DiaryWarningsPanel scope="mine" basePath="/barrister" />

      {/* ════════════════════════════════════════════════════════════
          HEARINGS NEEDING PREPARATION — Promoted to main view
         ════════════════════════════════════════════════════════════ */}
      {hearingsNeedingAttention.length > 0 && (
        <Card className="border-l-4 border-l-amber-400">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <WarningCircle className="h-4 w-4 text-amber-500" weight="fill" />
                <CardTitle>Hearings needing preparation</CardTitle>
              </div>
              <Badge variant="warning">{hearingsNeedingAttention.length}</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            {hearingsNeedingAttention.slice(0, 5).map((h) => (
              <Link
                key={h.hearingId}
                to={`/barrister/hearings/${h.hearingId}`}
                className="flex items-center justify-between gap-2 px-5 py-3 transition hover:bg-muted/30 border-b border-border/30 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{h.matterName}</div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{new Date(h.hearingDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                    <span>·</span>
                    <span className="text-amber-600 dark:text-amber-400 font-medium">
                      {h.missingCount} item{h.missingCount !== 1 ? 's' : ''} missing
                    </span>
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ${
                    h.percentage >= 75 ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' :
                    h.percentage >= 50 ? 'bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-300' :
                    'bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-300'
                  }`}>
                    {h.percentage}%
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                </div>
              </Link>
            ))}
            {hearingsNeedingAttention.length > 5 && (
              <Link
                to="/barrister/hearings"
                className="flex items-center justify-center gap-1 px-5 py-2.5 text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 border-t border-border/30"
              >
                View all {hearingsNeedingAttention.length} hearings <ArrowRight className="h-3 w-3" />
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      {/* Pending outcomes — operational debt.
          Withheld from pupils: assertPupilMayNotSetOutcome refuses them server-side, so "record
          now" was instructing them to do the one thing the role may not do. */}
      {pendingOutcomeHearings.length > 0 && canRecordHearingOutcome(user?.memberType) && (
        <Link
          to="/barrister/hearings"
          className="flex w-full items-center gap-3 rounded-lg border border-amber-200/60 dark:border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/10 px-4 py-3 text-xs text-amber-700 dark:text-amber-300 transition hover:bg-amber-50/60 dark:bg-amber-500/10"
        >
          <Warning className="h-4 w-4 shrink-0" weight="fill" />
          <span>
            {pendingOutcomeHearings.length} hearing{pendingOutcomeHearings.length > 1 ? 's' : ''} without a recorded outcome — record now
          </span>
          <ChevronRight className="ml-auto h-3.5 w-3.5 text-muted-foreground/50" />
        </Link>
      )}

      {/* ════════════════════════════════════════════════════════════
          WHAT NEEDS ATTENTION — a compact, state-aware stat strip. Each
          card stays calm (neutral) when there is nothing to do and only
          colours up when it needs the barrister: overdue tasks turn red,
          deadlines + unpaid fees turn amber. No wall of loud zeros on a
          quiet day. Tasks live HERE as cards — not duplicated in a
          separate panel (Rule #14: one source per fact). Every card
          navigates to its own page. The reads come from the SAME
          ['dashboard-metrics'] query, so there is no extra fetch.
         ════════════════════════════════════════════════════════════ */}
      {metrics && (
        <>
          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            <StatCard
              to="/barrister/tasks?filter=overdue"
              icon={<CheckSquare className="h-5 w-5" weight="fill" />}
              value={metrics.overdueTasks.count}
              label="Overdue tasks"
              tone={metrics.overdueTasks.count > 0 ? 'danger' : 'neutral'}
            />
            <StatCard
              to="/barrister/tasks"
              icon={<Clock className="h-5 w-5" weight="fill" />}
              value={metrics.upcomingDeadlines.count}
              label="Due this week"
              tone={metrics.upcomingDeadlines.count > 0 ? 'warning' : 'neutral'}
            />
            <StatCard
              to={metrics.unpaidFees.overdueCount > 0 ? '/barrister/fees?filter=unpaid' : '/barrister/fees'}
              icon={<CurrencyGbp className="h-5 w-5" weight="fill" />}
              value={new Intl.NumberFormat('en-GB', { style: 'currency', currency: metrics.currency, maximumFractionDigits: 0 }).format(
                metrics.unpaidFees.overdueCount > 0 ? metrics.unpaidFees.overdueAmount : metrics.unpaidFees.totalAmount,
              )}
              label={metrics.unpaidFees.overdueCount > 0 ? `${metrics.unpaidFees.overdueCount} overdue` : `${metrics.unpaidFees.count} unpaid`}
              tone={metrics.unpaidFees.overdueCount > 0 ? 'warning' : 'neutral'}
            />
            {/* This tile reads the matters STORE, not the metrics query, so a failed load would
                otherwise render a confident `0`. A dash says "not known", which is the truth;
                the banner above names why. A legitimate zero still renders as 0. */}
            <StatCard
              to="/barrister/matters"
              icon={<Briefcase className="h-5 w-5" weight="fill" />}
              value={mattersFailed ? '—' : myActiveMatters.length}
              label="Active matters"
              tone="neutral"
            />
          </div>

          {/* Quick navigation — directly beneath the strip, so the top of
              the page reads: your day → the numbers → where to go next. */}
          <div className="grid gap-3 sm:grid-cols-3">
            <Link
              to="/barrister/hearings"
              className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 transition hover:bg-muted/30"
            >
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Court list</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
            </Link>
            <Link
              to="/barrister/matters"
              className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 transition hover:bg-muted/30"
            >
              <div className="flex items-center gap-3">
                <Briefcase className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">My matters</span>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
            </Link>
            {/* Reports is refused to pupils and door tenants by REPORT_ROLES
                (reports.routes.ts), so only offer the shortcut to the roles the server serves —
                the same gate the sidebar applies. Grants nothing; the server stays in charge. */}
            {canUseReports && (
              <Link
                to="/barrister/reports"
                className="flex items-center justify-between rounded-lg border border-border bg-card px-4 py-3 transition hover:bg-muted/30"
              >
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">Practice reports</span>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
              </Link>
            )}
          </div>
        </>
      )}

      {/* ════════════════════════════════════════════════════════════
          UPDATES & ACTIVITY — what changed, what needs you, and clerk
          updates. Deliberately BELOW your hearings + cases: the
          information ("what have I got on") comes first; the follow-up
          actions come after.
         ════════════════════════════════════════════════════════════ */}
      <div className="flex items-center gap-3 pt-2">
        <h2 className="text-sm font-semibold text-muted-foreground">Updates &amp; activity</h2>
        <div className="h-px flex-1 bg-border/60" />
      </div>

      {/* Where to start — the judged Work Queue's first items. */}
      <MorningBrief />

      {/* Practice-wide money intelligence — what needs chasing today (quiet when nothing does). */}
      <MoneyRequiringAttention />

      {/* Money received but not yet matched to fee notes — allocate to complete revenue.
          Payments are closed to a pupil on BOTH sides (payments.routes.ts READ_ROLES and
          WRITE_ROLES exclude them), so this panel could only ever fire a 403 on every dashboard
          load and then render null. Not mounting it is what stops the request — the panel's own
          `useQuery` has no role to consult, and giving it one would push a permission decision
          into a shared component. */}
      {canAllocateFunds(user?.memberType) && <FundsToAllocate />}

      {/* Conference decisions the clerk needs from counsel — approve / decline / suggest */}
      <ConferenceApprovals />

      {!chambersMode.shouldHide.briefsInbox && <BriefsInbox />}

      {/* Operational alerts */}
      <OperationalAlerts />

      {/* Multi-matter context keeper — one-tap back to pinned + recently-opened matters. */}
      <MatterQuickSwitch />

    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────
// One tile in the "what needs attention" strip. Calm (neutral) by default;
// colours up only when the number needs the barrister. Always a link.
type StatTone = 'neutral' | 'danger' | 'warning';

const STAT_TONES: Record<StatTone, { card: string; iconBox: string; icon: string; value: string; label: string }> = {
  neutral: {
    card: 'border-border bg-card hover:bg-muted/30',
    iconBox: 'bg-brand-100 dark:bg-brand-500/20',
    icon: 'text-brand-600 dark:text-brand-400',
    value: 'text-foreground',
    label: 'text-muted-foreground',
  },
  danger: {
    card: 'border-red-200/80 bg-red-50/50 hover:bg-red-50/80 dark:border-red-500/30 dark:bg-red-500/10 dark:hover:bg-red-500/15',
    iconBox: 'bg-red-100 dark:bg-red-500/20',
    icon: 'text-red-600 dark:text-red-400',
    value: 'text-red-700 dark:text-red-400',
    label: 'font-medium text-red-600/80 dark:text-red-400/80',
  },
  warning: {
    card: 'border-amber-200/80 bg-amber-50/50 hover:bg-amber-50/80 dark:border-amber-500/30 dark:bg-amber-500/10 dark:hover:bg-amber-500/15',
    iconBox: 'bg-amber-100 dark:bg-amber-500/20',
    icon: 'text-amber-600 dark:text-amber-400',
    value: 'text-amber-700 dark:text-amber-400',
    label: 'font-medium text-amber-600/80 dark:text-amber-400/80',
  },
};

function StatCard({
  to, icon, value, label, tone,
}: { to: string; icon: ReactNode; value: ReactNode; label: string; tone: StatTone }) {
  const s = STAT_TONES[tone];
  return (
    <Link
      to={to}
      className={`flex items-center gap-3 rounded-xl border px-4 py-3.5 transition ${s.card}`}
    >
      <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${s.iconBox}`}>
        <span className={s.icon}>{icon}</span>
      </div>
      <div>
        <div className={`text-2xl font-bold tabular-nums ${s.value}`}>{value}</div>
        <div className={`text-xs ${s.label}`}>{label}</div>
      </div>
    </Link>
  );
}

// ─── Operational Alerts ───────────────────────────────────────────
function OperationalAlerts() {
  const navigate = useNavigate();
  const alertsQ = useQuery({
    queryKey: ['me-operational-alerts'],
    queryFn: fetchOperationalAlerts,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 10 * 60 * 1000,
  });
  const alerts = alertsQ.data ?? [];

  // This panel is the "something needs your attention" channel, and it renders NOTHING when
  // there is nothing wrong. A failed fetch produced the identical silence — the strongest
  // false all-clear on the page. Say it failed instead. A refusal (401/403/404) stays silent:
  // it means this role is not served by the endpoint, which is not a failure to report.
  const status = (alertsQ.error as { response?: { status?: number } } | null)?.response?.status;
  if (alertsQ.isError && status !== 401 && status !== 403 && status !== 404) {
    return <QueryErrorState compact what="your alerts" onRetry={() => void alertsQ.refetch()} />;
  }

  // Only show critical and warning alerts on dashboard
  const visible = alerts.filter(a => a.level === 'critical' || a.level === 'warning');
  if (visible.length === 0) return null;

  const ICONS = {
    critical: <WarningCircle className="h-4 w-4 shrink-0 text-red-600 dark:text-red-400" weight="fill" />,
    warning:  <Warning className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" weight="fill" />,
    info:     <Info className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" weight="fill" />,
  };
  const STYLES = {
    critical: 'border-red-200/80 dark:border-red-500/30 bg-red-50/50 dark:bg-red-500/10',
    warning:  'border-amber-200/80 dark:border-amber-500/30 bg-amber-50/40 dark:bg-amber-500/10',
    info:     'border-blue-200/60 dark:border-blue-500/30 bg-blue-50/30 dark:bg-blue-500/10',
  };

  return (
    <div className="space-y-2">
      {visible.slice(0, 3).map(alert => (
        <div key={alert.key}
          className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${STYLES[alert.level]}`}>
          {ICONS[alert.level]}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">{alert.title}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">{alert.body}</p>
          </div>
          <button
            onClick={() => navigate(alert.link)}
            className="flex shrink-0 items-center gap-1 rounded-lg border border-border/60 bg-card px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted transition">
            View <ArrowRight className="h-3 w-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
